/**
 * ParkingLanding — landing pubblica del parcheggio (route: /parcheggio/:id).
 * Layout ispirato alla PropertyLanding ma con palette BLU/azzurra per
 * distinguere visualmente il tipo di annuncio (parcheggio vs casa vacanze).
 * Include: Hero foto, caratteristiche tecniche, servizi, prezzi, contatti
 * diretti (email/telefono/WhatsApp), mappa con pin blu pulsante e JSON-LD
 * Schema.org tipo "Place" ottimizzato per SEO parking search.
 */
import { useState, useEffect, useMemo } from "react";
import { useParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Phone, Mail, MessageCircle, MapPin, Zap, Shield, Clock, ExternalLink, X, ChevronLeft, ChevronRight, Share2, Copy, Facebook, Send, Twitter, Linkedin, Check } from "lucide-react";
import axios from "axios";
import { API } from "../App";

const PROD_DOMAIN = "https://hostland.vitadahost.it";

const setMeta = (name, content, attr = "name") => {
  let el = document.querySelector(`meta[${attr}="${name}"]`);
  if (!el) { el = document.createElement("meta"); el.setAttribute(attr, name); document.head.appendChild(el); }
  el.setAttribute("content", content);
};
const setLdJson = (id, data) => {
  let el = document.getElementById(id);
  if (!el) { el = document.createElement("script"); el.type = "application/ld+json"; el.id = id; document.head.appendChild(el); }
  el.textContent = JSON.stringify(data);
};

export default function ParkingLanding() {
  const { parkingId } = useParams();
  const [data, setData] = useState({ parking: null, host: null });
  const [loading, setLoading] = useState(true);
  const [lightbox, setLightbox] = useState(null); // index foto aperta o null
  const [shareOpen, setShareOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    axios.get(`${API}/parkings/${parkingId}`).then(r => setData(r.data)).catch(() => setData({ parking: null })).finally(() => setLoading(false));
  }, [parkingId]);

  const p = data.parking;
  const city = useMemo(() => {
    if (!p?.comune) return "";
    return typeof p.comune === "string" ? p.comune : (p.comune?.nome || "");
  }, [p]);

  // SEO — dynamic head meta + JSON-LD Place schema
  useEffect(() => {
    if (!p) return;
    const canonical = `${PROD_DOMAIN}/parcheggio/${parkingId}`;
    const title = `${p.name} a ${city || "Italia"} — Parcheggio privato | Hostland ParkHost`;
    const desc = `${p.name}${p.address ? ` in ${p.address}` : ""}. Parcheggio privato ${p.parking_type || ""} con contatto diretto al proprietario. Zero commissioni.`;
    document.title = title;
    setMeta("description", desc);
    setMeta("robots", "index, follow, max-image-preview:large");
    setMeta("og:title", title, "property");
    setMeta("og:description", desc, "property");
    setMeta("og:url", canonical, "property");
    setMeta("og:type", "website", "property");
    setMeta("og:image", p.media?.[0] ? `${PROD_DOMAIN}/api/files/${p.media[0]}` : `${PROD_DOMAIN}/hostland-social.png`, "property");
    setMeta("og:locale", "it_IT", "property");
    let link = document.getElementById("parking-canonical");
    if (!link) { link = document.createElement("link"); link.id = "parking-canonical"; link.rel = "canonical"; document.head.appendChild(link); }
    link.href = canonical;
    const ld = {
      "@context": "https://schema.org",
      "@type": "ParkingFacility",
      name: p.name,
      url: canonical,
      description: p.description || desc,
      ...(p.media?.[0] ? { image: `${PROD_DOMAIN}/api/files/${p.media[0]}` } : {}),
      ...(p.address || city ? { address: { "@type": "PostalAddress", streetAddress: p.address || "", addressLocality: city, addressCountry: "IT" } } : {}),
      ...(p.location_lat && p.location_lng ? { geo: { "@type": "GeoCoordinates", latitude: p.location_lat, longitude: p.location_lng } } : {}),
      ...(p.phone ? { telephone: p.phone } : {}),
      ...(p.email ? { email: p.email } : {}),
    };
    setLdJson("parking-jsonld", ld);
    return () => { document.getElementById("parking-jsonld")?.remove(); };
  }, [p, parkingId, city]);

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-blue-50" data-prerender-pending="1"><div className="w-10 h-10 border-4 border-blue-200 border-t-blue-700 rounded-full animate-spin" /></div>;
  if (!p) return <div className="min-h-screen flex flex-col items-center justify-center bg-blue-50 p-6"><MapPin className="w-10 h-10 text-blue-300 mb-3" /><p className="text-lg font-bold">Parcheggio non trovato</p></div>;

  const services = [
    p.has_ev_charging && { icon: Zap, label: "Ricarica auto elettrica" },
    p.has_surveillance && { icon: Shield, label: "Sorvegliato 24h" },
    p.has_24h_access && { icon: Clock, label: "Accesso H24" },
    ...(p.custom_services || []).map(s => ({ icon: null, label: s })),
  ].filter(Boolean);

  // ─── Share helpers ───
  const shareUrl = typeof window !== "undefined" ? window.location.href : `https://hostland.vitadahost.it/parcheggio/${p.parking_id}`;
  const cityName = typeof p.comune === "string" ? p.comune : p.comune?.nome || "";
  const priceHint = p.price_mode === "fixed" && (p.price_yearly || p.price_monthly)
    ? (p.price_yearly ? `da €${Number(p.price_yearly).toFixed(0)}/anno` : `da €${Number(p.price_monthly).toFixed(0)}/mese`)
    : "";
  const shareTitle = `${p.name}${cityName ? ` — ${cityName}` : ""}`;
  const shareText =
    `🅿️ ${p.name}${cityName ? ` a ${cityName}` : ""}\n` +
    `${p.address ? p.address + "\n" : ""}` +
    `${priceHint ? priceHint + " · " : ""}Zero commissioni · Contatto diretto\n\n` +
    `Su Hostland ParkHost 👉`;

  const nativeShare = async () => {
    if (navigator.share) {
      try { await navigator.share({ title: shareTitle, text: shareText, url: shareUrl }); return; } catch { /* user cancel */ }
    }
    setShareOpen(true);
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(`${shareText} ${shareUrl}`);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch { /* silent */ }
  };

  const shareTargets = [
    { name: "WhatsApp", color: "linear-gradient(135deg,#25D366,#128C7E)", icon: MessageCircle,
      href: `https://wa.me/?text=${encodeURIComponent(`${shareText} ${shareUrl}`)}` },
    { name: "Facebook", color: "linear-gradient(135deg,#1877F2,#0C63D4)", icon: Facebook,
      href: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}&quote=${encodeURIComponent(shareText)}` },
    { name: "Telegram", color: "linear-gradient(135deg,#2AABEE,#229ED9)", icon: Send,
      href: `https://t.me/share/url?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(shareText)}` },
    { name: "X (Twitter)", color: "linear-gradient(135deg,#18181B,#000)", icon: Twitter,
      href: `https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(shareText)}` },
    { name: "LinkedIn", color: "linear-gradient(135deg,#0A66C2,#004182)", icon: Linkedin,
      href: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}` },
    { name: "Email", color: "linear-gradient(135deg,#52525B,#27272A)", icon: Mail,
      href: `mailto:?subject=${encodeURIComponent(shareTitle)}&body=${encodeURIComponent(`${shareText}\n\n${shareUrl}`)}` },
  ];

  return (
    <div className="min-h-screen" style={{ background: "linear-gradient(180deg, #EFF6FF 0%, #FFF 60%)" }}>
      {/* ─── HERO stile PropertyLanding, palette azzurra ─── */}
      {/* Layout foto multi-immagine: 1 primaria grande + 4 thumb accanto su desktop,
          carousel scrollabile su mobile. Sopra tutto: badge, titolo, indirizzo. */}
      <div className="max-w-6xl mx-auto px-4 md:px-6 pt-6 md:pt-10">
        {/* Titolo + indirizzo SOPRA le foto (come landing case) */}
        <div className="mb-4 md:mb-6" data-testid="parking-header">
          <div className="inline-flex items-center gap-2 mb-3 px-3 py-1 rounded-full bg-white border border-blue-200 shadow-sm">
            <span className="w-5 h-5 rounded-full bg-blue-700 text-white text-xs font-black flex items-center justify-center">P</span>
            <span className="text-[10px] font-black uppercase tracking-widest text-blue-800">Parcheggio · ParkHost</span>
          </div>
          <h1 className="text-3xl md:text-5xl font-black tracking-tight leading-tight text-zinc-900" data-testid="parking-landing-h1">{p.name}</h1>
          {p.address && (
            <p className="mt-2 text-sm md:text-base text-zinc-600 inline-flex items-center gap-1.5" data-testid="parking-address">
              <MapPin className="w-4 h-4 text-blue-700" />{p.address}
            </p>
          )}
        </div>

        {/* Photo Gallery — cornice blu chiaro coerente col rest */}
        <div className="rounded-3xl p-3 md:p-4" style={{ background: "linear-gradient(160deg, #DBEAFE, #BFDBFE)" }} data-testid="parking-gallery">
          {p.media && p.media.length > 0 ? (
            <>
              {/* Desktop: 1 primaria grande + grid 2×2 thumb */}
              <div className="hidden md:grid md:grid-cols-4 md:grid-rows-2 gap-2 md:gap-3" style={{ height: "clamp(320px, 55vh, 480px)" }}>
                <button
                  onClick={() => setLightbox(0)}
                  className="col-span-2 row-span-2 rounded-2xl overflow-hidden relative group"
                  data-testid="parking-hero-photo-0"
                >
                  <img src={`${API}/files/${p.media[0]}?w=1200`} alt={`${p.name} — foto principale`} className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-500" loading="eager" />
                </button>
                {[1, 2, 3, 4].map((idx) =>
                  p.media[idx] ? (
                    <button
                      key={idx}
                      onClick={() => setLightbox(idx)}
                      className="rounded-2xl overflow-hidden relative group"
                      data-testid={`parking-hero-photo-${idx}`}
                    >
                      <img src={`${API}/files/${p.media[idx]}?w=600`} alt={`${p.name} — foto ${idx + 1}`} className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-500" loading="lazy" />
                      {idx === 4 && p.media.length > 5 && (
                        <div className="absolute inset-0 bg-black/50 flex items-center justify-center text-white font-black text-lg">+{p.media.length - 5}</div>
                      )}
                    </button>
                  ) : (
                    <div key={idx} className="rounded-2xl bg-white/40" />
                  )
                )}
              </div>

              {/* Mobile: carousel snap orizzontale */}
              <div className="md:hidden flex gap-2 overflow-x-auto snap-x snap-mandatory" style={{ scrollbarWidth: "none" }} data-testid="parking-hero-carousel">
                {p.media.map((m, i) => (
                  <button key={m + i} onClick={() => setLightbox(i)} className="snap-start shrink-0 w-[85%] rounded-2xl overflow-hidden relative" style={{ aspectRatio: "4/3" }}>
                    <img src={`${API}/files/${m}?w=900`} alt={`${p.name} — foto ${i + 1}`} className="w-full h-full object-cover" loading={i === 0 ? "eager" : "lazy"} />
                    <div className="absolute bottom-2 right-2 px-2 py-0.5 rounded-full bg-black/60 text-white text-[10px] font-bold">{i + 1}/{p.media.length}</div>
                  </button>
                ))}
              </div>
            </>
          ) : (
            <div className="rounded-2xl flex items-center justify-center" style={{ aspectRatio: "16/9", background: "linear-gradient(135deg, #1E40AF, #0EA5E9)" }}>
              <span className="text-white text-9xl font-black">P</span>
            </div>
          )}
        </div>
      </div>

      <main className="max-w-4xl mx-auto px-5 md:px-8 py-8 md:py-12 space-y-6">
        {/* Caratteristiche */}
        <section className="bg-white rounded-3xl border border-blue-100 p-5 md:p-7 shadow-sm">
          <h2 className="text-xl font-black text-zinc-900 mb-4">Caratteristiche</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {p.parking_type && (
              <div className="rounded-2xl bg-blue-50 border border-blue-100 p-3 text-center">
                <p className="text-[10px] font-bold uppercase tracking-wider text-blue-700">Tipologia</p>
                <p className="text-sm font-black text-zinc-900 mt-1 capitalize">{p.parking_type}</p>
              </div>
            )}
            {p.access_mode && (
              <div className="rounded-2xl bg-blue-50 border border-blue-100 p-3 text-center">
                <p className="text-[10px] font-bold uppercase tracking-wider text-blue-700">Accesso</p>
                <p className="text-sm font-black text-zinc-900 mt-1 capitalize">{p.access_mode}</p>
              </div>
            )}
            {p.max_height && (
              <div className="rounded-2xl bg-blue-50 border border-blue-100 p-3 text-center">
                <p className="text-[10px] font-bold uppercase tracking-wider text-blue-700">Altezza max</p>
                <p className="text-sm font-black text-zinc-900 mt-1">{p.max_height} m</p>
              </div>
            )}
            {(p.max_width || p.max_length) && (
              <div className="rounded-2xl bg-blue-50 border border-blue-100 p-3 text-center">
                <p className="text-[10px] font-bold uppercase tracking-wider text-blue-700">Dimensioni</p>
                <p className="text-sm font-black text-zinc-900 mt-1">{p.max_width || "–"} × {p.max_length || "–"} m</p>
              </div>
            )}
          </div>
        </section>

        {/* Servizi */}
        {services.length > 0 && (
          <section className="bg-white rounded-3xl border border-blue-100 p-5 md:p-7 shadow-sm">
            <h2 className="text-xl font-black text-zinc-900 mb-4">Servizi inclusi</h2>
            <div className="flex flex-wrap gap-2">
              {services.map((s, i) => {
                const Ic = s.icon;
                return (
                  <span key={i} className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-blue-50 border border-blue-100 text-blue-800 text-sm font-bold">
                    {Ic && <Ic className="w-4 h-4" />}
                    {s.label}
                  </span>
                );
              })}
            </div>
          </section>
        )}

        {/* Descrizione */}
        {p.description && (
          <section className="bg-white rounded-3xl border border-blue-100 p-5 md:p-7 shadow-sm">
            <h2 className="text-xl font-black text-zinc-900 mb-3">Descrizione</h2>
            <p className="text-sm md:text-base text-zinc-700 whitespace-pre-line leading-relaxed">{p.description}</p>
          </section>
        )}

        {/* Prezzi */}
        <section className="rounded-3xl p-5 md:p-7 shadow-lg" style={{ background: "linear-gradient(135deg, #1E40AF, #0EA5E9)" }}>
          <h2 className="text-xl font-black text-white mb-3">Prezzi</h2>
          {p.price_mode === "on_request" ? (
            <p className="text-white/95 text-sm md:text-base">Prezzo su richiesta. Contatta direttamente il proprietario per un preventivo personalizzato.</p>
          ) : (
            <>
              <div className="grid grid-cols-3 gap-3">
                {p.price_daily && (
                  <div className="rounded-2xl bg-white/15 backdrop-blur border border-white/20 p-3 text-center text-white">
                    <p className="text-[10px] font-bold uppercase tracking-wider opacity-80">Al giorno</p>
                    <p className="text-2xl font-black mt-1">€{p.price_daily}</p>
                  </div>
                )}
                {p.price_monthly && (
                  <div className="rounded-2xl bg-white/15 backdrop-blur border border-white/20 p-3 text-center text-white">
                    <p className="text-[10px] font-bold uppercase tracking-wider opacity-80">Al mese</p>
                    <p className="text-2xl font-black mt-1">€{p.price_monthly}</p>
                  </div>
                )}
                {p.price_yearly && (
                  <div className="rounded-2xl bg-white/15 backdrop-blur border border-white/20 p-3 text-center text-white">
                    <p className="text-[10px] font-bold uppercase tracking-wider opacity-80">All'anno</p>
                    <p className="text-2xl font-black mt-1">€{p.price_yearly}</p>
                  </div>
                )}
              </div>
              {p.show_multi_day_contact && (
                <p className="mt-4 text-white/90 text-sm italic">Prezzi per più giorni da stabilire previo contatto.</p>
              )}
            </>
          )}
        </section>

        {/* Contatti */}
        <section className="bg-white rounded-3xl border border-blue-100 p-5 md:p-7 shadow-sm">
          <h2 className="text-xl font-black text-zinc-900 mb-1">Contatta il proprietario</h2>
          <p className="text-xs text-zinc-500 mb-4">Zero commissioni. Contatto diretto.</p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {p.phone && (
              <a href={`tel:${p.phone}`} className="inline-flex items-center gap-2 justify-center py-3 rounded-2xl text-white font-bold text-sm" style={{ background: "linear-gradient(135deg, #1E40AF, #0EA5E9)" }} data-testid="park-contact-phone">
                <Phone className="w-4 h-4" /> Chiama
              </a>
            )}
            {p.whatsapp && (
              <a href={`https://wa.me/${p.whatsapp.replace(/[^0-9+]/g, "")}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 justify-center py-3 rounded-2xl bg-emerald-500 text-white font-bold text-sm" data-testid="park-contact-wa">
                <MessageCircle className="w-4 h-4" /> WhatsApp
              </a>
            )}
            {p.email && (
              <a href={`mailto:${p.email}`} className="inline-flex items-center gap-2 justify-center py-3 rounded-2xl bg-zinc-900 text-white font-bold text-sm" data-testid="park-contact-email">
                <Mail className="w-4 h-4" /> Email
              </a>
            )}
          </div>
        </section>

        {/* ─── Big SHARE CTA ─── */}
        <section
          className="relative rounded-3xl overflow-hidden p-5 md:p-7 text-white"
          style={{ background: "linear-gradient(135deg, #1E3A8A 0%, #1E40AF 55%, #0EA5E9 100%)" }}
          data-testid="park-share-section"
        >
          {/* decorative blob */}
          <div className="absolute -top-16 -right-16 w-56 h-56 rounded-full bg-white/10 blur-3xl pointer-events-none" />
          <div className="flex flex-col md:flex-row items-start md:items-center gap-4">
            <div className="flex-1 min-w-0">
              <p className="text-[11px] font-black uppercase tracking-widest text-blue-200 mb-1.5">Aiuta altri automobilisti</p>
              <h2 className="text-xl md:text-2xl font-black tracking-tight leading-tight">Condividi questo parcheggio</h2>
              <p className="text-sm text-blue-100/90 mt-1.5 leading-relaxed">Un click per inviarlo su tutti i social con messaggio già pronto.</p>
            </div>
            <motion.button
              whileTap={{ scale: 0.95 }}
              whileHover={{ scale: 1.03 }}
              onClick={nativeShare}
              className="shrink-0 inline-flex items-center gap-2.5 bg-white text-blue-900 font-black text-base px-6 py-3.5 rounded-full shadow-2xl"
              data-testid="park-share-cta"
            >
              <Share2 className="w-5 h-5" />
              Condividi ora
            </motion.button>
          </div>
        </section>

        {/* Mappa con pin blu pulsante */}
        {p.location_lat && p.location_lng && (
          <section className="rounded-3xl overflow-hidden relative" style={{ background: "#0B1320", height: 260 }}>
            <iframe
              title="Mappa"
              src={`https://www.openstreetmap.org/export/embed.html?bbox=${p.location_lng - 0.02},${p.location_lat - 0.014},${p.location_lng + 0.02},${p.location_lat + 0.014}&layer=mapnik&marker=${p.location_lat},${p.location_lng}`}
              className="absolute inset-0 w-full h-full pointer-events-none"
              style={{ border: 0, filter: "grayscale(100%) brightness(0.55) contrast(1.15) saturate(0.4)" }}
            />
            <div className="absolute inset-0 pointer-events-none" style={{ background: "linear-gradient(180deg, rgba(11,19,32,0.35) 0%, rgba(11,19,32,0.15) 35%, rgba(11,19,32,0.85) 100%)" }} />
            {/* Blue pulsing pin — visual signature of ParkHost pins */}
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <motion.div className="rounded-full" style={{ width: 16, height: 16, background: "#2563EB", boxShadow: "0 0 0 5px rgba(37,99,235,0.35), 0 4px 12px rgba(37,99,235,0.55)" }}
                animate={{ scale: [1, 1.1, 1] }} transition={{ duration: 1.6, repeat: Infinity, ease: "easeInOut" }} />
              <motion.div className="absolute rounded-full" style={{ width: 16, height: 16, background: "#2563EB" }}
                animate={{ scale: [1, 3], opacity: [0.55, 0] }} transition={{ duration: 2, repeat: Infinity, ease: "easeOut" }} />
            </div>
            {p.address && (
              <div className="absolute inset-0 z-10 p-4 flex flex-col justify-between">
                <div>
                  <p className="text-[9px] font-bold uppercase tracking-[0.18em] text-white/75">Dove ti troverai</p>
                  <p className="text-white text-sm font-bold mt-0.5 tracking-tight truncate">{p.address}</p>
                </div>
                <a href={`https://www.openstreetmap.org/?mlat=${p.location_lat}&mlon=${p.location_lng}&zoom=16`} target="_blank" rel="noopener noreferrer"
                  className="self-start inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-bold bg-white text-zinc-900 pointer-events-auto shadow">
                  <MapPin className="w-3.5 h-3.5 text-blue-700" /> Apri in mappa <ExternalLink className="w-3 h-3 opacity-60" />
                </a>
              </div>
            )}
          </section>
        )}

        {/* Footer badge Hostland */}
        <div className="pt-4 pb-2 text-center">
          <a href={PROD_DOMAIN} className="inline-flex items-center gap-2 px-3.5 py-2 rounded-full bg-white border border-blue-100 shadow-sm" data-testid="parking-hostland-badge">
            <img src="/hostland-logo.png" alt="HostLand" className="w-5 h-5 rounded object-cover" />
            <span className="text-[11px] font-medium text-zinc-500">Il tuo spazio auto su <span className="font-bold text-zinc-900">Hostland</span></span>
          </a>
        </div>
      </main>

      {/* ─── Lightbox foto (aperto quando lightbox !== null) ─── */}
      <AnimatePresence>
        {lightbox !== null && p.media?.[lightbox] && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center p-4"
            onClick={() => setLightbox(null)}
            data-testid="parking-lightbox"
          >
            <button
              onClick={() => setLightbox(null)}
              className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center"
              data-testid="parking-lightbox-close"
            >
              <X className="w-5 h-5" />
            </button>
            {lightbox > 0 && (
              <button
                onClick={(e) => { e.stopPropagation(); setLightbox(lightbox - 1); }}
                className="absolute left-4 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
            )}
            {lightbox < p.media.length - 1 && (
              <button
                onClick={(e) => { e.stopPropagation(); setLightbox(lightbox + 1); }}
                className="absolute right-4 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            )}
            <motion.img
              key={lightbox}
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              src={`${API}/files/${p.media[lightbox]}?w=1600`}
              alt={`${p.name} — foto ${lightbox + 1}`}
              className="max-w-full max-h-[90vh] object-contain rounded-2xl"
              onClick={(e) => e.stopPropagation()}
            />
            <div className="absolute bottom-6 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-white/15 text-white text-xs font-bold">
              {lightbox + 1} / {p.media.length}
            </div>
          </motion.div>
        )}

        {/* Share modal — fallback for browsers without navigator.share */}
        {shareOpen && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={() => setShareOpen(false)}
            className="fixed inset-0 z-[200] flex items-end sm:items-center justify-center bg-black/70 backdrop-blur-sm px-4 py-6"
            data-testid="park-share-modal"
          >
            <motion.div
              initial={{ y: 40, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 40, opacity: 0 }}
              transition={{ type: "spring", stiffness: 280, damping: 26 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-3xl shadow-2xl w-full max-w-md p-6"
            >
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-xl font-black tracking-tight text-zinc-900">Condividi</h3>
                <button onClick={() => setShareOpen(false)} className="w-9 h-9 rounded-full bg-zinc-100 flex items-center justify-center hover:bg-zinc-200" data-testid="park-share-close">
                  <X className="w-4 h-4" />
                </button>
              </div>
              <div className="rounded-2xl bg-blue-50 border border-blue-100 p-3 mb-4">
                <p className="text-[10px] font-black uppercase tracking-widest text-blue-700 mb-1">Messaggio precompilato</p>
                <p className="text-xs text-zinc-700 leading-relaxed whitespace-pre-line line-clamp-4">{shareText}</p>
              </div>
              <div className="grid grid-cols-3 gap-2.5 mb-3">
                {shareTargets.map((t) => (
                  <a
                    key={t.name}
                    href={t.href}
                    target="_blank" rel="noopener noreferrer"
                    className="flex flex-col items-center gap-1.5 p-3 rounded-2xl bg-zinc-50 hover:bg-zinc-100 transition-colors"
                    data-testid={`park-share-${t.name.toLowerCase().replace(/[^a-z]/g, "")}`}
                  >
                    <div className="w-11 h-11 rounded-2xl flex items-center justify-center" style={{ background: t.color }}>
                      <t.icon className="w-5 h-5 text-white" />
                    </div>
                    <span className="text-[10px] font-bold text-zinc-700">{t.name}</span>
                  </a>
                ))}
              </div>
              <button
                onClick={copyLink}
                className={`w-full flex items-center justify-center gap-2 py-3 rounded-2xl font-bold text-sm transition-colors ${copied ? "bg-emerald-500 text-white" : "bg-zinc-900 text-white hover:bg-zinc-800"}`}
                data-testid="park-share-copy"
              >
                {copied ? <><Check className="w-4 h-4" /> Copiato!</> : <><Copy className="w-4 h-4" /> Copia link + messaggio</>}
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
